import { useEffect, useMemo, useRef, useState } from 'react';
import { usePermissions } from '@/shared/platform';
import './patients.css';
import { CompactBand, BandButton } from '@/components/CompactBand';
import { NumberedPager, ListSkeleton, useListUrlState, applySearch, paginate, sortBy } from '@/components/lists';
import {
  PATIENTS,
  VISITING_COUNT,
  NOT_READY_COUNT,
  NOT_READY_STATES,
  dotKind,
  type PatientRow,
} from './patientsMock';

const PAGE_SIZE = 50;
const TOTAL = PATIENTS.length;

/** The payers present in the mock (one shared payer list, D168). */
const PAYER_OPTIONS = [...new Set(PATIENTS.map((p) => p.primaryPayer).filter(Boolean))].sort((a, b) => a.localeCompare(b));

/** Insurance-state <select> options (rcm-24 §3). */
const STATE_OPTIONS: { value: string; label: string; test: (p: PatientRow) => boolean }[] = [
  { value: '', label: 'Any insurance state', test: () => true },
  { value: 'not_ready', label: 'Not ready', test: (p) => NOT_READY_STATES.includes(p.insState) },
  { value: 'no_insurance', label: 'No insurance on file', test: (p) => p.insState === 'no_insurance' },
  { value: 'coverage_ended', label: 'Coverage ended', test: (p) => p.insState === 'coverage_ended' },
  { value: 'not_checked', label: 'Not checked yet', test: (p) => p.insState === 'not_checked' },
];

/** Every string a search should match for a row (rcm-24 §3, §6). */
function textOf(p: PatientRow): string {
  const digits = p.phone.replace(/\D/g, '');
  const dobDash = p.dobLabel.replace(/\//g, '-');
  // DOB as typed in short forms too: "10/14/1988" → "10/14/88".
  const dobShort = p.dobLabel.replace(/\/(\d{2})(\d{2})$/, '/$2');
  return [
    p.name, p.lastFirst, p.lastName,
    p.dobLabel, dobDash, dobShort,
    p.phone, digits,
    p.memberId, p.chart,
    p.primaryPayer, p.location,
  ].join(' ');
}

function stateTest(value: string): (p: PatientRow) => boolean {
  return STATE_OPTIONS.find((s) => s.value === value)?.test ?? (() => true);
}

export function PatientsDirectory({ onSelect }: { onSelect: (id: string) => void }) {
  const { role } = usePermissions();
  // Export is for owners and admins only (D152). Unknown role (demo/staging)
  // is treated as allowed; staff and providers cannot export.
  const canExport = role !== 'staff' && role !== 'provider';

  const [ls, patch] = useListUrlState({ q: '', filter: 'visiting', state: '', payer: '', page: '1' });
  const page = Math.max(1, Number(ls.page) || 1);
  const visitingOn = ls.filter === 'visiting';

  // Simulated server-search latency: 8 skeleton rows while a query settles.
  const [loading, setLoading] = useState(false);
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setLoading(true);
    const t = window.setTimeout(() => { setLoading(false); }, 260);
    return () => { window.clearTimeout(t); };
  }, [ls.q]);

  const filtered = useMemo(() => {
    let rows = PATIENTS;
    if (visitingOn) rows = rows.filter((p) => p.bucket === 'today' || p.bucket === 'tomorrow');
    rows = rows.filter(stateTest(ls.state));
    if (ls.payer !== '') rows = rows.filter((p) => p.primaryPayer === ls.payer);
    rows = applySearch(rows, ls.q, textOf);
    // Default sort: next visit when the visiting filter is on, else last name.
    return visitingOn
      ? sortBy(rows, (p) => p.visitSort, 1)
      : sortBy(rows, (p) => p.lastFirst.toLowerCase(), 1);
  }, [visitingOn, ls.state, ls.payer, ls.q]);

  const pageRows = useMemo(() => paginate(filtered, page, PAGE_SIZE), [filtered, page]);

  const set = (next: Partial<Record<'q' | 'filter' | 'state' | 'payer' | 'page', string>>) => {
    patch({ page: '1', ...next });
  };

  const headline = (
    <>
      {TOTAL.toLocaleString()} patients.{' '}
      <em>
        {VISITING_COUNT} visit today or tomorrow, {NOT_READY_COUNT} aren’t ready.
      </em>
    </>
  );

  const exportCsv = () => {
    const header = ['Name', 'DOB', 'Chart', 'Phone', 'Next visit', 'Provider', 'Primary payer', 'Member ID', 'Insurance', 'Yearly max left'];
    const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const lines = filtered.map((p) =>
      [
        p.name, p.dobLabel, p.chart, p.phone,
        p.bucket === 'none' ? 'None booked' : p.nextVisitLabel,
        p.nextVisitProvider, p.primaryPayer || '—', p.memberId || '—', p.insLabel,
        p.yearlyMaxLeft == null ? '—' : `$${p.yearlyMaxLeft.toLocaleString('en-US')} left`,
      ].map((c) => esc(c)).join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `patients-${String(filtered.length)}-rows.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    // Every export is logged (D152).
    console.log(`Export logged: ${String(filtered.length)} patient rows exported by ${role ?? 'current user'}.`);
  };

  return (
    <div className="pdir">
      <CompactBand
        headline={headline}
        right={canExport ? <BandButton variant="g" onClick={exportCsv}>Export CSV</BandButton> : undefined}
      />

      <div className="pdir-scroll">
        <section className="pdir-card">
          <div className="pdir-fb">
            <input
              className="pdir-inp pdir-search"
              type="search"
              value={ls.q}
              placeholder="Name, date of birth, phone, member ID or chart number"
              aria-label="Search patients"
              onChange={(e) => { set({ q: e.target.value }); }}
            />
            {visitingOn ? (
              <button
                type="button"
                className="pdir-chip on"
                onClick={() => { set({ filter: 'all' }); }}
              >
                Visiting today or tomorrow <span aria-hidden>✕</span>
                <span className="pdir-sr">Clear the visiting filter</span>
              </button>
            ) : null}
            <select className="pdir-sel" aria-label="Insurance state" value={ls.state} onChange={(e) => { set({ state: e.target.value }); }}>
              {STATE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select className="pdir-sel" aria-label="Payer" value={ls.payer} onChange={(e) => { set({ payer: e.target.value }); }}>
              <option value="">All payers</option>
              {PAYER_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {visitingOn ? (
            <p className="pdir-hint">Clear the filter to search all {TOTAL.toLocaleString()} patients.</p>
          ) : null}

          {loading ? (
            <div className="pdir-skel"><ListSkeleton rows={8} /></div>
          ) : filtered.length === 0 ? (
            <div className="pdir-empty">
              <div className="pdir-empty-title">
                {ls.q.trim() !== '' ? `No patient matches “${ls.q}”.` : 'No patients match these filters.'}
              </div>
            </div>
          ) : (
            <>
              <table className="pdir-tb">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Next visit</th>
                    <th>Primary insurance</th>
                    <th>Insurance</th>
                    <th className="rt">Yearly max</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((p) => (
                    <tr key={p.id} className="pdir-row" tabIndex={0} role="link"
                      onClick={() => { onSelect(p.id); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(p.id); } }}
                    >
                      <td>
                        <span className="pdir-name">{p.name}</span>
                        <span className="pdir-sub">DOB {p.dobLabel} · chart {p.chart}</span>
                      </td>
                      <td>
                        {p.bucket === 'none'
                          ? <span className="pdir-muted">None booked</span>
                          : <>{p.nextVisitLabel} · {p.nextVisitProvider}</>}
                      </td>
                      <td>
                        {p.primaryPayer === ''
                          ? <span className="pdir-muted">—</span>
                          : <>{p.primaryPayer}{p.memberId ? <> · <span className="pdir-m">{p.memberId}</span></> : null}</>}
                      </td>
                      <td>
                        <span className="pdir-st">
                          <i className={`pdir-dot ${dotKind(p.insState)}`} aria-hidden />
                          {p.insLabel}
                        </span>
                      </td>
                      <td className="rt pdir-m">
                        {p.yearlyMaxLeft == null ? <span className="pdir-muted">—</span> : `$${p.yearlyMaxLeft.toLocaleString('en-US')} left`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="pdir-pg">
                <NumberedPager
                  page={page}
                  pageSize={PAGE_SIZE}
                  total={filtered.length}
                  onPage={(n) => { patch({ page: String(n) }); }}
                />
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
